import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API, errText, useAuth } from "@/lib/auth";
import { ErrorBox, Freshness, InsightCard, PriceChart, Skel, compact, money, pctTxt } from "@/components/market/Shared";

const StarBtn = ({ id }) => {
  const { profile, toggleWatch } = useAuth();
  const on = profile?.watchlist?.includes(id);
  const click = async (e) => {
    e.preventDefault();
    if (!profile) return toast.info("Sign in to use watchlists");
    try { await toggleWatch(id); } catch (err) { toast.error(errText(err)); }
  };
  return <button onClick={click} aria-label="Watch" data-testid={`star-${id}`} className="p-1.5"><Star className={`w-4 h-4 ${on ? "fill-[var(--violet)] text-[var(--violet)]" : "text-slate-500"}`} /></button>;
};

export function Markets() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);
  const load = () => axios.get(`${API}/crypto/markets`, { params: { limit: 250 } }).then((r) => { setD(r.data); setErr(null); }).catch(() => setErr("Market list unavailable right now."));
  useEffect(() => { load(); const id = setInterval(load, 120000); return () => clearInterval(id); }, []);
  const rows = useMemo(() => (d?.assets || []).filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase()) || a.symbol.toLowerCase().includes(q.toLowerCase())), [d, q]);
  return (
    <div className="space-y-4" data-testid="markets-page">
      <h1 className="font-head font-bold text-3xl tracking-tight">Markets</h1>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 250 coins by name or symbol" className="pl-9 h-12 bg-[var(--card)] border-[var(--line)] rounded-xl" data-testid="markets-search-input" />
      </div>
      {err && <ErrorBox msg={err} onRetry={load} />}
      {d && <Freshness d={d} testid="markets-freshness" />}
      <section className="glass divide-y divide-[var(--line)]" data-testid="markets-list">
        {!d && !err ? [1, 2, 3, 4, 5].map((i) => <div key={i} className="p-3"><Skel h="h-10" /></div>) :
          rows.length === 0 ? <p className="p-6 text-sm text-slate-400" data-testid="markets-empty">No coins match "{q}".</p> :
            rows.slice(0, 100).map((a) => (
              <Link key={a.id} to={`/asset/${a.id}`} className="flex items-center gap-3 px-3 sm:px-4 py-3 hover:bg-white/[0.02] transition-colors" data-testid={`market-row-${a.id}`}>
                <span className="text-xs text-slate-500 w-6 font-num">{a.rank}</span>
                <img src={a.image} alt="" className="w-7 h-7 rounded-full" />
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{a.name}</div><div className="text-xs text-slate-500">{a.symbol} · vol {compact(a.volume_24h)}</div></div>
                <div className="text-right font-num"><div className="text-sm">{money(a.price)}</div>
                  <div className={`text-xs ${a.change_24h_pct >= 0 ? "text-emerald-300" : "text-red-300"}`}>{pctTxt(a.change_24h_pct)}</div></div>
                <StarBtn id={a.id} />
              </Link>
            ))}
      </section>
      {rows.length > 100 && <p className="text-xs text-slate-500">Showing 100 of {rows.length}. Refine your search.</p>}
    </div>
  );
}

export function Asset() {
  const { id } = useParams();
  const { profile, viewed } = useAuth();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setA(null);
    axios.get(`${API}/crypto/asset/${id}`).then((r) => setA(r.data)).catch((e) => setErr(errText(e)));
    viewed(id).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <ErrorBox msg={err} />;
  if (!a) return <Skel h="h-96" />;
  return (
    <div className="space-y-4" data-testid="asset-page">
      <div className="flex items-center gap-3">
        {a.image && <img src={a.image} alt="" className="w-10 h-10 rounded-full" />}
        <div className="flex-1"><h1 className="font-head font-bold text-2xl" data-testid="asset-name">{a.name} <span className="text-slate-500 text-base">{a.symbol}</span></h1></div>
        <StarBtn id={a.id} />
      </div>
      <section className="glass p-4 sm:p-6">
        <div className="font-num text-4xl font-semibold" data-testid="asset-price">{money(a.price)}</div>
        <div className={`font-num text-sm ${a.change_24h_pct >= 0 ? "text-emerald-300" : "text-red-300"}`}>{pctTxt(a.change_24h_pct)} (24h)</div>
        <div className="grid grid-cols-2 gap-3 mt-4 text-xs font-num text-slate-400">
          <div>24h volume <div className="text-slate-100 text-sm">{compact(a.volume_24h)}</div></div>
          {a.market_cap != null ? <div>Market cap <div className="text-slate-100 text-sm">{compact(a.market_cap)}</div></div> :
            <div>24h range <div className="text-slate-100 text-sm">{money(a.low_24h)} – {money(a.high_24h)}</div></div>}
        </div>
        <div className="mt-4"><PriceChart asset={a.id} /></div>
        <div className="mt-2"><Freshness d={a} testid="asset-freshness" /></div>
      </section>
      <InsightCard asset={a.id} signedIn={!!profile} />
      <Link to="/alerts" state={{ asset: a.id }} className="block text-center py-3 rounded-full border border-[var(--line)] text-sm hover:border-[var(--cyan)]" data-testid="asset-alert-link">Set a price alert for {a.symbol}</Link>
    </div>
  );
}
